import { createContext, ReactNode, useEffect, useReducer, useState } from "react";
import { ActionMap, AuthState, AuthContextType } from "types/auth.types";
import { ApiUser } from "types/api-user";
import FullPageLoader from "components/loaders/FullPageLoader";
import { useAuth0 } from "@auth0/auth0-react";
import { useDatadogRum } from "react-datadog";
import Error401Page from "pages/errors/Error401Page";
import { APP_URLS } from "urls/AppUrls";
import Error403Page from "pages/errors/Error403Page";
import Error404Page from "pages/errors/Error404Page";
import Error500Page from "pages/errors/Error500Page";
import { useGetApiToken } from "hooks/useAuth";
import { DEFAULT_LOGIN_OPTIONS, DEFAULT_SILENT_TOKEN_OPTIONS_INTELLISIGHT_API } from "../constants/auth-constants";
import { useGetMe } from "../api-hooks/api-users.hooks";

const logoutUrl = process.env.REACT_APP_LOGOUT_URL ?? window.location.origin;

declare global {
  interface Window {
    pendo?: any;
  }
}

enum ACTION_TYPES {
  INITIAL = "INITIALIZE",
  AUTHENTICATED = "AUTHENTICATED",
  USER_SET = "USER_SET",
  LOGIN_FAILED = "LOGINFAILED",
  LOGOUT = "LOGOUT",
}

type AuthPayLoad = {
  [ACTION_TYPES.INITIAL]: undefined;
  [ACTION_TYPES.AUTHENTICATED]: undefined;
  [ACTION_TYPES.USER_SET]: {
    user: ApiUser
  },
  [ACTION_TYPES.LOGIN_FAILED]: undefined;
  [ACTION_TYPES.LOGOUT]: undefined;
};

export type AuthActions = ActionMap<AuthPayLoad>[keyof ActionMap<AuthPayLoad>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  hasError: false,
};

type AuthReducerType = (state: AuthState, action: AuthActions) => AuthState;

const AuthReducer: AuthReducerType = (state, action) => {
  console.log("AuthReducer action", action, state);
  switch (action.type) {
    case ACTION_TYPES.INITIAL:
      return {
        ...state,
        isInitialized: true,
        hasError: false,
      };
    case ACTION_TYPES.AUTHENTICATED:
      return {
        ...state,
        isAuthenticated: true,
        isInitialized: true,
        hasError: false,
      }
    case ACTION_TYPES.USER_SET:
      return {
        ...state,
        isAuthenticated: true,
        isInitialized: true,
        user: action.payload.user,
        hasError: false,
      }
    case ACTION_TYPES.LOGIN_FAILED:
      return {
        ...state,
        isInitialized: true,
        isAuthenticated: false,
        user: null,
        hasError: true,
      };
    case ACTION_TYPES.LOGOUT:
      return {
        ...state,
        isInitialized: false,
        isAuthenticated: false,
        user: null,
        hasError: false,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};


function AuthProvider({ children }: AuthProviderProps) {
  const { isAuthenticated, logout, loginWithRedirect } = useAuth0();
  const [isProcessingLogin, setIsProcessingLogin] = useState(false);
  const [state, dispatch] = useReducer(
    AuthReducer,
    { ...initialState, isAuthenticated }
  );
  const getMeQuery = useGetMe({ enabled: state.isAuthenticated && !isProcessingLogin });
  const datadog = useDatadogRum();
  const { getApiToken } = useGetApiToken();

  useEffect(() => {
    if (isAuthenticated) {
      dispatch({
        type: ACTION_TYPES.AUTHENTICATED
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const initialize = async () => {
      try {
        if (state.isAuthenticated && !isProcessingLogin) {
          setIsProcessingLogin(true);
          const token = await getApiToken(DEFAULT_SILENT_TOKEN_OPTIONS_INTELLISIGHT_API, true);

          if (!token) {

            console.error("No token received from Auth0 inside AuthProvider");
            await loginWithRedirect(DEFAULT_LOGIN_OPTIONS);
          }

        }
        return await Promise.resolve();
      } catch (err) {
        console.error(err);

        // Always dispatch LOGIN_FAILED on error
        dispatch({
          type: ACTION_TYPES.LOGIN_FAILED,
        });

        if (
          err?.message === 'User data not found' ||
          err?.message === 'No user organizations found'
        ) {
          console.error("Primary data for the user was not found. We are going to re-login the user.");
          await loginWithRedirect(DEFAULT_LOGIN_OPTIONS);
          // Only after a true Auth0 logout, LOGOUT will be dispatched elsewhere
        }

        if (err.response) {
          if (err.response.status === 401) {
            console.error(err,
              "Check that this user is actually in the database and that they have the rights to access this resource"
            );
            window.location.hash = APP_URLS.PAGES.ERROR_401; //config.loginRoute;
          } else if (err.response.status === 403) {
            window.location.hash = APP_URLS.PAGES.ERROR_403;
          } else if (err.response.status === 404) {
            window.location.hash = APP_URLS.PAGES.ERROR_404;
          } else if (err.response.status === 500) {
            window.location.hash = APP_URLS.PAGES.ERROR_500;
          }

          return await Promise.reject(err);
        }
      } finally {
        setIsProcessingLogin(false);
      }
    };

    initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isAuthenticated]);

  // Set the user when loaded
  useEffect(() => {
    if (getMeQuery.isSuccess && getMeQuery.data) {
      dispatch({
        type: ACTION_TYPES.USER_SET,
        payload: {
          user: getMeQuery.data,
        },
      });
    }
  }, [getMeQuery.data, getMeQuery.isSuccess]);

  useEffect(() => {
    if (state.isAuthenticated && state.user) {

      datadog.setUser({
        id: `${state.user?.id}`,
        email: state.user?.email,
      });

      //pendo configure
      window.pendo.initialize({
        // TODO: When we get the new snowflake Platform Services ID, we should put it here
        visitor: {
          id: `${state.user?.id}`,
          email: state.user?.email,
        },

        // TODO: When we get the new snowflake Platform Services ID, we should put it here
        account: {
          // id: `${user.id}`,
        },
      });
    }
  }, [state.isAuthenticated, state.user, datadog]);

  const triggerLogin = async () => {
    await loginWithRedirect(DEFAULT_LOGIN_OPTIONS);
  };

  const triggerLogout = async () => {
    dispatch({ type: ACTION_TYPES.LOGOUT });
    await logout({ logoutParams: { returnTo: logoutUrl } });
  };


  const renderContent = () => {
    if (window.location.hash.includes(APP_URLS.PAGES.ERROR_401)) {
      return <Error401Page />;
    }

    if (window.location.hash.includes(APP_URLS.PAGES.ERROR_403)) {
      return <Error403Page hideHomeButton={true} />;
    }

    if (window.location.hash.includes(APP_URLS.PAGES.ERROR_404)) {
      return <Error404Page />;
    }

    if (window.location.hash.includes(APP_URLS.PAGES.ERROR_500)) {
      return <Error500Page />;
    }

    if ((state.isAuthenticated && state.user)) {
      return <>{children}</>;
    }

    console.log("AuthContext rendering FullPageLoader - waiting for authentication or error", state);

    return <FullPageLoader />;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        triggerLogin,
        triggerLogout,
      }}
    >
      {renderContent()}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
